import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CreditCard, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { billingAPI } from '../lib/api';
import { LogoPublic } from '../components/Logo';

/**
 * Card collection page shown after signup when TRIAL_REQUIRES_CARD=true.
 * Redirects to Stripe Checkout in setup/subscription mode.
 * If user already completed setup, redirects to dashboard.
 */
export const TrialSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check if card setup is actually needed
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await billingAPI.getStatus();
        if (!res.data.card_required_pending) {
          // Card already collected or not required — go to dashboard
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // If status check fails, show the page anyway
      }
      setCheckingStatus(false);
    };
    checkStatus();
  }, [navigate]);

  const handleSetupCard = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await billingAPI.createTrialCheckout(window.location.origin);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Failed to create checkout session');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <LogoPublic size="lg" />
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold font-heading">
              One last step
            </CardTitle>
            <CardDescription className="text-base">
              Add a payment method to activate your free trial
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md" data-testid="trial-setup-error">
                {error}
              </div>
            )}

            {/* Value props */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Shield className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="text-sm font-medium">No charge today</p>
                  <p className="text-xs text-muted-foreground">Your card is only used to verify your identity</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm font-medium">14-day free trial</p>
                  <p className="text-xs text-muted-foreground">Full access to all features during your trial</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-medium">Cancel anytime</p>
                  <p className="text-xs text-muted-foreground">No commitment — cancel before the trial ends and you won't be charged</p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSetupCard}
              className="w-full h-12 text-base font-bold"
              disabled={loading}
              data-testid="setup-card-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting to secure checkout...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Payment Method
                </>
              )}
            </Button>

            <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
              You'll be redirected to Stripe's secure checkout page.
              Your payment information is handled directly by Stripe — we never see your card details.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
